// src/components/AppRouter.js
import React from "react";
import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { getAllRoutes, ROUTE_PERMISSION_MAP } from "../config/routes";
import MainLayout from "./Layout/MainLayout";

// ✅ NEW: maintenance imports
import { useMaintenance } from "../contexts/MaintenanceContext";
import MaintenanceBreak from "./UI/MaintenanceBreak";

import Login from "./Pages/Login";
import Dashboard from "./Pages/Dashboard";
import Users from "./Pages/Users";
import Children from "./Pages/Children";
import Development from "./Pages/Development";
import Alerts from "./Pages/Alerts";
import Donations from "./Pages/Donations";
import Reports from "./Pages/Reports";
import Setting from "./Pages/Setting";
import Profile from "./Pages/Profile";

import DonateCheckout from "./Pages/DonateCheckout";
import DonateSuccess from "./Pages/DonateSuccess";

import ForgotPassword from "./Pages/ForgotPassword";
import ResetPassword from "./Pages/ResetPassword";
import Notifications from "./Pages/Notifications";

import PrivacyPolicy from "./Pages/PrivacyPolicy";
import TermsOfService from "./Pages/TermsOfService";
import Help from "./Pages/Help";

import ChildDetails from "./Pages/ChildDetails";

const Settings = () => (
  <div className="p-6">
    <h1 className="text-2xl font-bold">Settings</h1>
  </div>
);

const componentMap = {
  Dashboard,
  Children,
  Development,
  Alerts,
  Donations,
  Reports,
  Users,
  Setting,
  Settings,
  Profile,
  Notifications,
};

const hasFullAccess = (user) =>
  Array.isArray(user?.permissions) && user.permissions.includes("Full Access");

const hasPermission = (user, perm) => {
  if (!perm) return true;
  if (hasFullAccess(user)) return true;
  return Array.isArray(user?.permissions) && user.permissions.includes(perm);
};

// ✅ loading UI reused
const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500" />
  </div>
);

// ✅ PROTECTED wrapper using Outlet (best practice)
const ProtectedLayout = () => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <MainLayout>
      <Outlet />
    </MainLayout>
  );
};

// ✅ PUBLIC wrapper for login page only
const PublicOnlyRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  return !user ? <Outlet /> : <Navigate to="/dashboard" replace />;
};

// ✅ permission gate wrapper
const PermissionGate = ({ permission }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (!hasPermission(user, permission)) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
};

export default function AppRouter() {
  // ✅ Maintenance break for non-admin only
  const { maintenance } = useMaintenance();
  const { user } = useAuth();

  const role = String(user?.role || "").toLowerCase();
  const shouldBlock = maintenance?.active && role !== "admin";

  if (shouldBlock) return <MaintenanceBreak />;

  const allRoutes = getAllRoutes();

  // ✅ IMPORTANT: remove public donation routes from protected mapping
  const protectedRoutes = allRoutes.filter(
    (r) => r?.path && r.path !== "/donate" && r.path !== "/donate-success"
  );

  return (
    <Routes>
      {/* ✅ PUBLIC DONATION PAGES (community access) */}
      <Route path="/donate" element={<DonateCheckout />} />
      <Route path="/donate-success" element={<DonateSuccess />} />

      {/* PUBLIC (login only, if already logged in -> /dashboard) */}
      <Route element={<PublicOnlyRoute />}>
        <Route path="/login" element={<Login />} />
      </Route>

      {/* ✅ allow these even if logged in */}
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* ✅ PROTECTED APP */}
      <Route element={<ProtectedLayout />}>
      <Route path="/children/:id" element={<ChildDetails />} />
        {protectedRoutes.map((route) => {
          const Component = componentMap[route.component];
          const requiredPerm = ROUTE_PERMISSION_MAP[route.path] ?? null;

          // If component missing, show a debug page
          const Page = Component ? <Component /> : <div className="p-6">Component not found</div>;

          // Permission wrapper only when required
          if (requiredPerm) {
            return (
              <Route key={route.path} element={<PermissionGate permission={requiredPerm} />}>
                <Route path={route.path} element={Page} />
              </Route>
            );
          }

          // No permission required (but still logged-in)
          return <Route key={route.path} path={route.path} element={Page} />;
        })}

        {/* Default inside app */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
      </Route>

      {/* fallback */}
      <Route path="*" element={<Navigate to="/login" replace />} />

      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/help" element={<Help />} />
    </Routes>
  );
}